import semver from 'semver';

import { createContext, useContext, useMemo } from 'react';
import { stringify } from 'qs';
import { useBoolean } from 'react-use';

type WebViewBridgeContextValue = {
  setAppBarTop: (payload: AppBarTopPayload) => void;
  setVisibleAppBarTop: (payload: isVisibleAppBarTopPayload) => void;
  isClasstingRNWebView: () => boolean;
  open: (payload: OpenParams) => void;
  close: (payload?: EventPayload) => void;
  gteTargetVersion: (targetVersion: string) => boolean;
  setClassAppBarTop: (classroomId: string, title?: string) => void;
  setAppBarTopClickedEvent: (payload: EventPayload) => void;
  setEnablePullToRefresh: (enabled: boolean) => void;
  isAuthenticated: boolean;
  setAuthenticated: (isAuthenticated: boolean) => void;
};

type WebViewBridgeProviderProps = {
  children: React.ReactNode;
};

type OpenParams = {
  url: string;
  transition?: 'stack' | 'sheet' | 'drawer';
  appbar?: boolean;
  toolbar?: boolean;
};

type EventPayload = {
  eventName: string;
  eventDetail?: any;
};

type AppBarButtonConfig = {
  link?: string;
  action?: 'push' | 'pop' | 'popToMain';
};

type AppBarLabelButtonConfig = AppBarButtonConfig & {
  label: string;
};

type AppBarIconButtonConfig = AppBarButtonConfig & {
  icon: 'home' | 'menu' | 'chat' | 'setting';
};

type AppBarTopPayload = {
  title: string;
  subTitle?: string;
  leftButton?: AppBarLabelButtonConfig | AppBarIconButtonConfig | null;
  rightButton?: AppBarLabelButtonConfig | AppBarIconButtonConfig | null;
};

type isVisibleAppBarTopPayload = {
  isVisible: boolean;
};

type AppBarTopMessageObject = {
  category: string;
  type: 'app_bar_top';
  payload: AppBarTopPayload;
};

type CommonMessageObject = {
  category: string;
  type: 'change_config';
  payload?: {
    toolbar?: {
      isVisible: boolean;
    };
    appbar?: {
      isVisible: boolean;
    };
  };
};

type CloseMessageObject = {
  category: string;
  type: 'close';
  payload?: EventPayload;
};

type AppBarTopClickedMessageObject = {
  category: string;
  type: 'app_bar_top_clicked';
  payload: EventPayload;
};

function isClasstingRNWebView(): boolean {
  return window.top.navigator?.userAgent?.includes('classting_rn') ?? false;
}

function getClasstingRNAppVersion(toNumber = false): string | number {
  if (!window.top) {
    return '';
  }

  const { userAgent } = window.top.navigator;

  const regex = /classting_rn,\s(\d+).(\d+).(\d+)/;
  const match = regex.exec(userAgent.toLowerCase());

  if (toNumber) {
    return match ? Number(match.slice(1, 4).join('')) : 0;
  }

  return match ? match.slice(1, 4).join('.') : '';
}

function gteTargetVersion(targetVersion: string): boolean {
  if (!isClasstingRNWebView()) {
    return false;
  }

  const appVersion = getClasstingRNAppVersion() as string;

  return semver.gte(appVersion, targetVersion);
}

function setAppBarTop(payload: AppBarTopPayload) {
  const targetVersion = '7.17.0';

  if (gteTargetVersion(targetVersion)) {
    const message: AppBarTopMessageObject = {
      category: 'webView',
      type: 'app_bar_top',
      payload,
    };

    window.top.ReactNativeWebView?.postMessage(JSON.stringify(message));
  }
}
function setVisibleAppBarTop({ isVisible }: isVisibleAppBarTopPayload) {
  if (isClasstingRNWebView()) {
    const message: CommonMessageObject = {
      category: 'webView',
      type: 'change_config',
      payload: {
        appbar: {
          isVisible,
        },
      },
    };

    window.top.ReactNativeWebView?.postMessage(JSON.stringify(message));
  }
}

function setEnablePullToRefresh(enable: boolean) {
  if (isClasstingRNWebView()) {
    const message = {
      category: 'webView',
      type: 'change_config',
      payload: {
        enablePullToRefresh: enable,
      },
    };

    window.top.ReactNativeWebView?.postMessage(JSON.stringify(message));
  }
}

function open({
  url: targetUrl,
  transition = 'stack',
  appbar = true,
  toolbar = false,
}: OpenParams) {
  if (isClasstingRNWebView()) {
    const url = new URL(targetUrl);
    url.searchParams.append('hide_app_bar_top', appbar ? '1' : '0');

    const queryString = stringify({
      url: url.toString(),
      transition_type: transition,
      appbar: appbar ? 1 : 0,
      toolbar: toolbar ? 1 : 0,
    });

    window.location.href = `classting://webview?${queryString}`;
  }
}

function close(payload?: EventPayload) {
  if (isClasstingRNWebView()) {
    const message: CloseMessageObject = {
      category: 'navigation',
      type: 'close',
      payload,
    };

    window.top.ReactNativeWebView?.postMessage(JSON.stringify(message));
  }
}

function setClassAppBarTop(classroomId: string, title: string = '') {
  setVisibleAppBarTop({ isVisible: true });
  setAppBarTop({
    title,
    leftButton: {
      icon: 'menu',
      link: `classting://webview?url=${encodeURIComponent(
        `${window.location.origin}/classrooms/${classroomId}/app-navigation-menu`
      )}&transition_type=drawer&appbar=1`,
    },
    rightButton: {
      icon: 'home',
      action: 'popToMain',
    },
  });
}

function setAppBarTopClickedEvent(payload: EventPayload) {
  if (isClasstingRNWebView()) {
    const message: AppBarTopClickedMessageObject = {
      category: 'webView',
      type: 'app_bar_top_clicked',
      payload,
    };

    window.top.ReactNativeWebView?.postMessage(JSON.stringify(message));
  }
}

const WebViewBridgeContext = createContext<WebViewBridgeContextValue | null>({
  setAppBarTop,
  setVisibleAppBarTop,
  isClasstingRNWebView,
  open,
  close,
  gteTargetVersion,
  setClassAppBarTop,
  setAppBarTopClickedEvent,
  setEnablePullToRefresh,
  isAuthenticated: false,
  setAuthenticated: (isAuthenticated: boolean) => {},
});

export default function WebViewBridge() {
  function WebViewBridgeProvider({ children }: WebViewBridgeProviderProps) {
    const [isAuthenticated, setAuthenticated] = useBoolean(false);

    const value = useMemo(
      () => ({
        setAppBarTop,
        setVisibleAppBarTop,
        isClasstingRNWebView,
        open,
        close,
        gteTargetVersion,
        setClassAppBarTop,
        setAppBarTopClickedEvent,
        isAuthenticated,
        setAuthenticated,
        setEnablePullToRefresh,
      }),
      [isAuthenticated, setAuthenticated]
    );

    return (
      <WebViewBridgeContext.Provider value={value}>
        {children}
      </WebViewBridgeContext.Provider>
    );
  }

  function useWebViewBridge() {
    const context = useContext(WebViewBridgeContext);

    if (!context) {
      throw new Error(
        `useWebViewBridge must be used within an WebViewBridgeProvider`
      );
    }

    return context;
  }

  return {
    WebViewBridgeProvider,
    WebViewBridgeConsumer: WebViewBridgeContext.Consumer,
    useWebViewBridge,
  };
}
